import { Alert, AlertTitle, BottomNavigation, BottomNavigationAction, Container, Divider, Paper, Zoom } from "@mui/material";
import BookIcon from '@mui/icons-material/Book';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StoreIcon from '@mui/icons-material/Store';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CategoryIcon from '@mui/icons-material/Category';
import { useState } from "react";
import { styled, alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import InputBase from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { getOfficeMeta, getPageWarning, getUserAccess, hitTraffic } from "./common";
import Unauthorized from "../unauthorized";
import Loading2 from "./loading2";
import { useNavigate } from "react-router-dom";
import { getLanguage, getName, getRole, setLanguage } from "../authorization/auth";
import { APP_NAME } from "../authorization/const";
import { logout } from "../authorization/login_halper";
import { Helmet } from "react-helmet";

const pages = [
  { "label": 'Products', "link": "/products", icon: <CategoryIcon /> },
  { "label": 'Pricing', "link": "/pricing", icon: <ShoppingCartIcon /> },
  { "label": 'ServiceNow', "link": "/service", icon: <BookIcon /> }
];

const settings = [
  { "label": 'Profile', "link": "/profile" },
  { "label": 'Account', "link": "/profile/reset-pass" },
  { "label": 'Dashboard', "link": "/lagacy-dashboard" },
  { "label": 'Logout', "link": "/logout" }
];

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: 7,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: '40%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '30ch',
      },
    },
  },
}));

const getActiveMenu = (pathname) => {
  for (let idx = 0; idx < pages.length; idx++) {
    const element = pages[idx];
    if (element.link === pathname) return pathname;
  }
  return "/dashboard";
}

const changeLanguage = (lang) => {
  if (getLanguage() === lang) return;
  setLanguage(lang);
  window.location.reload();
}


/**
 * Fame for all screen
 * 
 * @param {string} permission Permission required to open the page. When undefined opens without authintication.
 * @param {string} children Body of tag
 * @param {string} onSearch Call this function when searched on navbar.
 * @param {string} onInit Call this function when initlization complete. Parameter (auth) true when permission is verified otherwise false.
 * @param {string} screen Name of the screen for warning and traffice logging.
 * @param {boolean} login if login is required in this page
 * @param {string} title HTML page title
 * @returns Object
 */
const MenuFrame = ({ permission, children, onSearch, onInit, screen, login = true, title = "Courier CRM" }) => {
  const [authorized, setAuthorized] = useState(true);
  const [value, setValue] = useState(getActiveMenu(window.location.pathname));
  const [userMenu, setUserMenu] = useState(null);
  const [logo, setLogo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [headWarning, setHeadWarning] = useState(null);
  const navigator = useNavigate();

  const handleOpenUserMenu = (event) => {
    setUserMenu(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setUserMenu(null);
  };

  const handleChange = (event, newValue) => {
    navigator(newValue);
    setValue(newValue);
  };

  async function initLoginUser() {
    const meta = await getOfficeMeta();
    if (meta.logo) setLogo(meta.logo);
    let auth = false;
    if (permission) {
      auth = await getUserAccess(permission);
      setAuthorized(auth);
    }
    if (typeof onInit === "function") await onInit(auth);
    if (!!screen) {
      getPageWarning(screen).then((warn) => {
        if (warn.mode) setHeadWarning(warn);
      }).catch(async (err) => {
        console.error(err);
        await logout();
        navigator("/");
      });
      hitTraffic(screen);
    }
    return true;
  }

  if (logo === null) {
    setLogo("/logo192.png");
    if (login) {
      initLoginUser().then((_) => {
        setIsLoading(false);
      }).catch((err) => {
        setIsLoading(false);
        console.error(err);
      });
    } else {
      setIsLoading(false);
      getPageWarning(screen).then((warn) => {
        if (warn.mode) setHeadWarning(warn);
      });
      hitTraffic(screen);
    }
  }

  if (!authorized) return <Unauthorized />;
  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: 'primary.background' }}>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Container maxWidth="xl">
            <Toolbar disableGutters>
              <Box onClick={() => { navigator("/dashboard"); }} style={{ cursor: "pointer" }}>
                <img src={logo === null ? "/logo192.png" : logo} style={{ display: 'flex', mr: 1, height: 32, borderRadius: 5 }} alt="logo" />
              </Box>
              <Typography
                variant="h6" noWrap style={{ cursor: "pointer" }}
                onClick={() => { navigator("/dashboard"); }}
                sx={{ ml: 1, mr: 2, display: 'flex', flexGrow: { xs: 1, md: 'initial' }, fontFamily: 'monospace', fontWeight: 700, letterSpacing: '.3rem', color: 'inherit', textDecoration: 'none', }}
              >
                {logo === null || logo === "/logo192.png" ? "DTDC" : ""}
              </Typography>
              <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                {pages.map((page, idx) => (
                  <Button key={idx} onClick={() => { navigator(page.link); }} style={{ cursor: "pointer" }} sx={{ my: 2, color: 'white', display: 'block' }} >
                    {page.label}
                  </Button>
                ))}
              </Box>
              <Search sx={{ mr: 2 }}>
                <SearchIconWrapper>
                  <SearchIcon />
                </SearchIconWrapper>
                <StyledInputBase placeholder="Search..." onKeyUp={onSearch} slotProps={{ htmlInput: { 'aria-label': 'search' } }} />
              </Search>
              <Box sx={{ flexGrow: 0 }}>
                <Tooltip title="Open settings">
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar alt="SD" src="/noimage.jpg" />
                  </IconButton>
                </Tooltip>
                {login ?
                  <Menu sx={{ mt: '45px' }} id="menu-appbar" anchorEl={userMenu} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} keepMounted transformOrigin={{ vertical: 'top', horizontal: 'right' }} open={Boolean(userMenu)} onClose={handleCloseUserMenu}>
                    <Box sx={{ m: 2, minWidth: 200 }}>
                      <Typography variant="subtitle1">{getName()}</Typography>
                      <Typography variant="caption">{getRole()} - {APP_NAME}</Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ m: 2, minWidth: 200 }}>
                      <Typography onClick={() => { changeLanguage("en"); }} variant="caption" style={{ cursor: "pointer" }}>English</Typography>
                      <Typography onClick={() => { changeLanguage("hi"); }} variant="caption" style={{ cursor: "pointer" }}> | Hindi</Typography>
                    </Box>
                    <Divider />
                    {settings.map((setting, idx) => (
                      <MenuItem key={idx} onClick={handleCloseUserMenu} component='a' href={setting.link}>
                        <Typography sx={{ textAlign: 'center' }}>{setting.label}</Typography>
                      </MenuItem>
                    ))}
                  </Menu> : ""
                }
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </Box>

      <Container maxWidth="lg" sx={{ px: 1, py: 1, pb: { xs: "70px", md: "initial" } }}>
        {
          headWarning === null ? "" :
            <Box>
              <Zoom in={true}>
                <Alert severity={headWarning.mode}>
                  {headWarning.title ? <AlertTitle>{headWarning.title}</AlertTitle> : ""} {headWarning.message}
                </Alert>
              </Zoom>
            </Box>
        }
        {isLoading ? <Loading2 /> : children}
      </Container>
      <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, display: { sm: 'initial', md: "none" } }} elevation={2}>
        <BottomNavigation value={value} onChange={handleChange} >
          <BottomNavigationAction label="Store" value="/dashboard" icon={<StoreIcon />} />
          {pages.map((page, idx) => (
            <BottomNavigationAction key={idx} label={page.label} value={page.link} icon={page.icon} />
          ))}
          <BottomNavigationAction label="Scan" value="/scanner" icon={<QrCodeScannerIcon />} />
        </BottomNavigation>
      </Paper>
    </Box>
  );
};

export default MenuFrame;
